/**
 * Model Rollback Service
 * 
 * Service for managing ML model rollback operations (platform admin only).
 * Connects to BRAIN backend /api/platform-admin/ml/models endpoints.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Uses getBaseUrl() for dynamic domain resolution
 * - Defensive null checks on all API responses
 * - Proper error handling with try/catch
 * - Network retry with exponential backoff
 */

import { getBaseUrl } from '../shared/utils/apiConfig';

export interface ModelVersion {
  model_id: string;
  version: string;
  algorithm: string;
  deployment_status: 'pending' | 'deploying' | 'deployed' | 'failed' | 'stopped';
  deployed_at: string | null;
  registered_at: string;
  metrics: Record<string, unknown> | null;
}

export interface ModelVersionHistory {
  algorithm: string;
  tenant_id: string;
  current_version: ModelVersion | null;
  available_versions: ModelVersion[];
  total_versions: number;
}

export interface RollbackRequest {
  target_version?: string;
  reason: string;
}

export interface RollbackResponse {
  model_id: string;
  algorithm: string;
  previous_version: string;
  new_version: string;
  rollback_reason: string;
  rolled_back_at: string;
}

export interface RollbackHistoryItem {
  model_id: string;
  algorithm: string;
  from_version: string;
  to_version: string;
  reason: string;
  rolled_back_by: string;
  rolled_back_at: string;
}

export interface RollbackHistoryResponse {
  history: RollbackHistoryItem[];
  total: number;
}

export interface DeploymentStatus {
  tenant_id: string;
  deployed_models: number;
  models: Array<{
    model_id: string;
    algorithm: string;
    version: string;
    deployed_at: string | null;
    metrics: Record<string, unknown> | null;
  }>;
}

// Network configuration constants
const NETWORK_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY_MS: 1000,
  REQUEST_TIMEOUT_MS: 30000,
} as const;

class ModelRollbackService {
  private baseUrl: string;
  private maxRetries: number = NETWORK_CONFIG.MAX_RETRIES;
  private retryDelay: number = NETWORK_CONFIG.RETRY_DELAY_MS;

  constructor() {
    this.baseUrl = getBaseUrl();
  }

  /**
   * Get authorization headers with platform admin token
   */
  private getHeaders(): HeadersInit {
    const token = localStorage.getItem('astrapeak_platform_admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  /**
   * Retry fetch with exponential backoff for network errors
   */
  private async fetchWithRetry(
    url: string,
    options: RequestInit,
    retries: number = this.maxRetries
  ): Promise<Response> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), NETWORK_CONFIG.REQUEST_TIMEOUT_MS);
        
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        const isLastAttempt = attempt === retries;
        
        if (error instanceof Error) {
          const isNetworkError = 
            error.name === 'TypeError' || 
            error.name === 'AbortError' ||
            error.message.includes('fetch') ||
            error.message.includes('network');
          
          if (isNetworkError && !isLastAttempt) {
            const delay = this.retryDelay * Math.pow(2, attempt);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
        }
        
        throw error;
      }
    }
    
    throw new Error('Max retries exceeded');
  }

  /**
   * Get version history for a specific algorithm
   */
  async getModelVersions(
    tenantId: string,
    algorithm: string
  ): Promise<ModelVersionHistory> {
    const url = `${this.baseUrl}/api/platform-admin/ml/models/${tenantId}/versions/${algorithm}`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`No models found for algorithm: ${algorithm}`);
      }
      throw new Error(`Failed to get model versions: ${response.statusText}`);
    }

    const data = await response.json();
    
    return {
      algorithm: data.algorithm ?? algorithm,
      tenant_id: data.tenant_id ?? tenantId,
      current_version: data.current_version ?? null,
      available_versions: Array.isArray(data.available_versions) ? data.available_versions : [],
      total_versions: data.total_versions ?? 0,
    };
  }

  /**
   * Rollback model to previous or specific version
   * 
   * Includes idempotency check to handle network failures gracefully.
   * If the request fails but the rollback actually succeeded on the backend,
   * this method will detect it and return success instead of throwing an error.
   */
  async rollbackModel(
    tenantId: string,
    algorithm: string,
    request: RollbackRequest
  ): Promise<RollbackResponse> {
    const url = `${this.baseUrl}/api/platform-admin/ml/models/${tenantId}/rollback/${algorithm}`;
    
    try {
      const response = await this.fetchWithRetry(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        if (response.status === 400) {
          const error = await response.json();
          throw new Error(error.detail || 'Invalid rollback request');
        }
        if (response.status === 404) {
          throw new Error(`Model or target version not found`);
        }
        throw new Error(`Failed to rollback model: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      // CRITICAL: Idempotency check
      // If network failed, verify if rollback actually succeeded on backend
      try {
        const history = await this.getRollbackHistory(tenantId, algorithm, 1, 0);
        const latestRollback = history.history[0];
        
        // Check if the latest rollback matches our request
        if (latestRollback && 
            latestRollback.to_version === request.target_version &&
            latestRollback.algorithm === algorithm) {
          
          // Rollback succeeded despite network error
          // Return success response constructed from history
          return {
            model_id: latestRollback.model_id,
            algorithm: algorithm,
            previous_version: latestRollback.from_version,
            new_version: latestRollback.to_version,
            rollback_reason: request.reason,
            rolled_back_at: latestRollback.rolled_back_at,
          };
        }
      } catch (historyErr) {
        // If we can't verify, throw original error
      }
      
      // Rollback definitely failed, throw original error
      throw err;
    }
  }

  /**
   * Get rollback history for tenant
   */
  async getRollbackHistory(
    tenantId: string,
    algorithm?: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<RollbackHistoryResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    
    if (algorithm) {
      params.append('algorithm', algorithm);
    }

    const url = `${this.baseUrl}/api/platform-admin/ml/models/${tenantId}/rollback-history?${params.toString()}`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get rollback history: ${response.statusText}`);
    }

    const data = await response.json();
    
    return {
      history: Array.isArray(data.history) ? data.history : [],
      total: data.total ?? 0,
    };
  }

  /**
   * Manually trigger model reload in inference engine
   */
  async reloadModel(
    tenantId: string,
    algorithm: string
  ): Promise<{ message: string; tenant_id: string; algorithm: string }> {
    const url = `${this.baseUrl}/api/platform-admin/ml/models/${tenantId}/reload/${algorithm}`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to reload model: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get deployment status for all models of a tenant
   */
  async getDeploymentStatus(tenantId: string): Promise<DeploymentStatus> {
    const url = `${this.baseUrl}/api/platform-admin/ml/models/${tenantId}/deployment-status`;
    
    const response = await this.fetchWithRetry(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get deployment status: ${response.statusText}`);
    }

    const data = await response.json();
    
    return {
      tenant_id: data.tenant_id ?? tenantId,
      deployed_models: data.deployed_models ?? 0,
      models: Array.isArray(data.models) ? data.models : [],
    };
  }
}

export const modelRollbackService = new ModelRollbackService();
export default modelRollbackService;
